import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Alert, Button, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { requirementsApi } from '../../services/api';
import IdChip from '../common/IdChip';
import ItemTooltip from '../itemCard/ItemTooltip';
import TypeChipLink from '../common/TypeChipLink';
import useTypeMap from '../../hooks/useTypeMap';
import { usePref } from '../../services/prefs';
import { htmlToText } from '../../utils/htmlText';
import { downloadTextFile, downloadBlob } from '../../utils/exportChat';
import {
  attributeEntries, attributeValueText, typeLabelFor, exportFileBase,
  subtreeToCsv, buildPrintHtml, openPrintWindow,
} from '../../utils/documentExport';

// Heading level = tree depth, capped at 6 — same visual contract as the
// Markdown exporter / Breakdown tab.
function headingTag(depth) {
  return `h${Math.min(depth + 1, 6)}`;
}
function headingFontSize(depth) {
  if (depth === 0) return 20;
  if (depth === 1) return 17;
  if (depth === 2) return 15;
  return 13.5;
}

// Type chip — same look as the Breakdown tab's type chip (ItemBlock) /
// ItemDetails' #231 type chit.
const TYPE_CHIP_SX = {
  fontSize: 10, fontWeight: 600, color: 'text.secondary', border: 1,
  borderColor: 'divider', borderRadius: 0.5, px: 0.5, textTransform: 'uppercase',
  letterSpacing: '.04em', whiteSpace: 'nowrap',
};

/**
 * One item of the document: title row (title + standardised id chip + type
 * chip on the SAME row, #341), plain-text description, a clean two-column
 * attribute/value table, then the children recursively.
 */
function DocumentNode({ node, depth, typeMap, showHumanIds }) {
  const description = htmlToText(node.description || '');
  const attrs = attributeEntries(node);
  return (
    <Box sx={{ ml: depth > 0 ? 2.5 : 0, mb: 2 }} data-testid={`doc-node-${node.id}`}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
        data-testid={`doc-title-row-${node.id}`}
      >
        <Typography
          component={headingTag(depth)}
          sx={{ fontWeight: 700, fontSize: headingFontSize(depth), m: 0, letterSpacing: '-0.01em' }}
        >
          {node.title || 'Untitled'}
        </Typography>
        {/* Standardised id display (#174): resolveIdDisplay via IdChip honours
            the showHumanIds pref; the quick-action ItemTooltip supplies the
            hover mini-card + navigation, so the raw GUID is never shown inline. */}
        <ItemTooltip item={{ id: node.id, display_id: node.display_id, title: node.title, type: node.type }}>
          <IdChip
            id={node.id}
            displayId={node.display_id}
            showHumanIds={showHumanIds}
            title={node.title || ''}
            disableTooltip
          />
        </ItemTooltip>
        <TypeChipLink typeId={node.type_id} label={typeLabelFor(node, typeMap)}>
          <Box component="span" sx={TYPE_CHIP_SX} data-testid={`doc-type-chip-${node.id}`}>
            {typeLabelFor(node, typeMap)}
          </Box>
        </TypeChipLink>
      </Box>

      {description && (
        <Typography sx={{ whiteSpace: 'pre-wrap', my: 0.75, lineHeight: 1.55, fontSize: 14 }}>
          {description}
        </Typography>
      )}

      {attrs.length > 0 && (
        // Horizontal-scroll guard so an extreme value can never overflow the
        // tab; normal long values wrap inside their cell (word-break).
        <Box sx={{ overflowX: 'auto', my: 1 }}>
          <Box
            component="table"
            data-testid={`doc-attrs-${node.id}`}
            sx={{
              borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed',
              '& th, & td': {
                border: '1px solid', borderColor: 'divider', p: '4px 8px',
                textAlign: 'left', verticalAlign: 'top', fontSize: 13,
                wordBreak: 'break-word',
              },
              '& th': { bgcolor: 'action.hover', fontWeight: 600 },
              '& th:first-of-type, & td:first-of-type': { width: '30%' },
            }}
          >
            <thead>
              <tr><th scope="col">Attribute</th><th scope="col">Value</th></tr>
            </thead>
            <tbody>
              {attrs.map(([name, value]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td>{attributeValueText(value)}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}

      {(node.children || []).map((child) => (
        <DocumentNode
          key={child.id}
          node={child}
          depth={depth + 1}
          typeMap={typeMap}
          showHumanIds={showHumanIds}
        />
      ))}
    </Box>
  );
}

DocumentNode.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string,
    display_id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    attributes: PropTypes.object,
    type_id: PropTypes.string,
    type: PropTypes.string,
    children: PropTypes.array,
  }).isRequired,
  depth: PropTypes.number.isRequired,
  typeMap: PropTypes.object.isRequired,
  showHumanIds: PropTypes.bool,
};

/**
 * DocumentTab — a neatly-formatted, read-only document view of a container and
 * all of its sub-items (#341): rendered from the structured subtree export
 * (`GET /api/items/{id}/export/json`) so each entry shows the standardised id
 * chip (+tooltip) on the title row, an item-type chip and a proper
 * attribute/value table — plus an export toolbar (.MD / PDF / Excel / ReqIF).
 *
 * LAZY: mounted by `ItemDetails` only while its tab is active (TabPanel mounts
 * children only when selected); the fetch fires from an effect.
 *
 * CACHING: the cache is owned by the parent (`ItemDetails`) via the
 * `getCached`/`setCached` props — a TabPanel unmounts its children on tab
 * switch, so a component-local cache would be discarded. The cached value is
 * the subtree object.
 */
const DocumentTab = ({ itemId, getCached, setCached }) => {
  const [subtree, setSubtree] = useState(() => (getCached ? getCached(itemId) : undefined));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exportError, setExportError] = useState(null);
  const typeMap = useTypeMap();
  const showHumanIds = usePref('showHumanIds');

  const load = useCallback(async (id, { force = false } = {}) => {
    if (!id) return;
    const cached = getCached ? getCached(id) : undefined;
    if (!force && cached !== undefined) {
      setSubtree(cached);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await requirementsApi.exportItemSubtree(id);
      const tree = (data && data.subtree) || null;
      if (setCached) setCached(id, tree);
      setSubtree(tree);
    } catch (err) {
      setError(`Could not load document: ${err.message}`);
      setSubtree(null);
    } finally {
      setLoading(false);
    }
  }, [getCached, setCached]);

  // Fetch when this tab is mounted/active for an item. Because the parent only
  // mounts us while the Document tab is selected, this is the lazy trigger.
  useEffect(() => {
    load(itemId);
    setExportError(null);
  }, [itemId, load]);

  const fileBase = exportFileBase(subtree, itemId);

  const handleExportMarkdown = useCallback(async () => {
    setExportError(null);
    try {
      const data = await requirementsApi.exportItemMarkdown(itemId);
      downloadTextFile(`${fileBase}.md`, (data && data.content) || '', 'text/markdown');
    } catch (err) {
      setExportError(`Markdown export failed: ${err.message}`);
    }
  }, [itemId, fileBase]);

  // "Export to PDF" = a print-optimised rendering + the browser's print dialog
  // (save as PDF) — deliberately no heavy PDF library.
  const handleExportPdf = useCallback(() => {
    setExportError(null);
    if (!subtree) return;
    const ok = openPrintWindow(buildPrintHtml(subtree, { typeMap, showHumanIds }));
    if (!ok) setExportError('Could not open the print view (popup blocked?).');
  }, [subtree, typeMap, showHumanIds]);

  // Excel-compatible CSV (UTF-8 BOM) generated client-side from the subtree.
  const handleExportExcel = useCallback(() => {
    setExportError(null);
    if (!subtree) return;
    downloadTextFile(
      `${fileBase}.csv`,
      subtreeToCsv(subtree, { typeMap, showHumanIds }),
      'text/csv;charset=utf-8'
    );
  }, [subtree, typeMap, showHumanIds, fileBase]);

  const handleExportReqif = useCallback(async () => {
    setExportError(null);
    try {
      const data = await requirementsApi.exportItemReqif(itemId);
      downloadTextFile(`${fileBase}.reqif`, (data && data.content) || '', 'application/xml');
    } catch (err) {
      setExportError(`ReqIF export failed: ${err.message}`);
    }
  }, [itemId, fileBase]);

  // #381 follow-up: Word (.docx) — real binary Blob streamed by the server.
  const handleExportWord = useCallback(async () => {
    setExportError(null);
    try {
      const blob = await requirementsApi.exportItemDocx(itemId);
      downloadBlob(`${fileBase}.docx`, blob);
    } catch (err) {
      setExportError(`Word export failed: ${err.message}`);
    }
  }, [itemId, fileBase]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }} data-testid="document-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 1 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => load(itemId, { force: true })}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!subtree) {
    return <div className="t-body muted3" style={{ marginTop: 8 }}>No document content available for this container.</div>;
  }

  return (
    <Box sx={{ mt: 1 }} data-testid="document-content">
      <Box
        sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}
        data-testid="document-export-toolbar"
      >
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportMarkdown} data-testid="export-md">
          Export to .MD
        </Button>
        <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={handleExportPdf} data-testid="export-pdf">
          Export to PDF
        </Button>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportExcel} data-testid="export-excel">
          Export to Excel
        </Button>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportWord} data-testid="export-word">
          Export to Word
        </Button>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportReqif} data-testid="export-reqif">
          Export to ReqIF
        </Button>
      </Box>
      {exportError && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setExportError(null)} data-testid="document-export-error">
          {exportError}
        </Alert>
      )}
      <DocumentNode node={subtree} depth={0} typeMap={typeMap} showHumanIds={showHumanIds} />
    </Box>
  );
};

DocumentTab.propTypes = {
  itemId: PropTypes.string,
  // Parent-owned cache accessors (survive TabPanel unmount on tab switch).
  // getCached(id) -> cached subtree | undefined; setCached(id, tree) -> void.
  getCached: PropTypes.func,
  setCached: PropTypes.func,
};

export default DocumentTab;
